/**
 * Desktop (`lg` and up) view of the Metal Resources landing's Metal Market Intelligence section,
 * chosen by `../MetalMarketIntelligence.tsx`: `imageUrl` as a background banner with `title`
 * over it, the description, a wrapping row of category tiles (list image with a hover overlay
 * and a colour badge), and the button link. The mobile view is
 * `../MetalMarketIIntelligenceMobile/MetalMarketIntelligenceMobile.tsx`.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { ContentSection, Flex } from "@ryerson/frontend.layout";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Link, ButtonLink } from "@ryerson/frontend.navigation";
import { MetalMarketIntelligenceProps } from "../MetalMarketIntelligence";
import { Badge } from "@ryerson/frontend.notification";
import { BlogCategoryMin } from "@contentful/blog/types";
import Spacer from "@components/Shared/Spacer/Spacer";

const IntelDiv = styled.div`
	position: relative;
	display: flex;
	width: 20%;
	height: 100px;
	margin-bottom: 40px;
`;

const BadgeContainer = styled.div`
	position: absolute;
	z-index: 1;
	width: 200px;
	left: 0;
	bottom: 0%;
`;


/** One category tile, linking to its category page. Expects `listPageImage` to be present. */
const Intel = (props: BlogCategoryMin) => {
	return (
		<IntelDiv>
			<Link to={"/metal-resources/metal-market-intelligence/" + props.slug}>
				<div
					css={css`
						background-image: url(${props.listPageImage.file.url});
						width: 200px;
						border-radius: 2px;
						height: 80px;
						object-fit: cover;
						&:hover {
							.overlay {
								visibility: visible !important;
								opacity: 0.5 !important;
							}
						}
					`}
				>
					<div
						className="overlay"
						css={css`
							width: 200px;
							height: 80px;
							visibility: hidden;
							position: absolute;
							background-color: rgb(0, 0, 0);
							opacity: 0;
							top: 0;
							left: 0;
							transition: 0.15s visibility linear, 0.15s opacity linear;
						`}
					/>
				</div>
				<BadgeContainer>
					<Badge badgeType="pill" type="tertiary" color={props.categoryColor}>
						{props.title}
					</Badge>
				</BadgeContainer>
			</Link>
		</IntelDiv>
	);
};

/** The desktop section; see the file header. */
const MetalMarketIntelligenceDesktop: React.FC<
	MetalMarketIntelligenceProps & { blogCategories: BlogCategoryMin[] }
> = (props) => {
	const { theme } = useTheme();
	return (
		<>
			<div
				css={css`
					background-image: url(${props.imageUrl});
					background-size: cover;
					background-repeat: no-repeat;
					background-position: center;
					margin: 0 auto;
					height: 251px;
					max-width: 1440px;
					width: 100%;
					padding-top: 120px;
					box-sizing: border-box;
				`}
			>
				<Typography
					variant="h1"
					type="tertiary"
					css={css`
						width: 100%;
						max-width: 1160px;
						margin: 0 auto;
						letter-space: -4%;
					`}
				>
					{props.title}
				</Typography>
			</div>
			<ContentSection type="secondary" vPadding="30px">
				<Typography
					variant="p"
					css={css`
						margin-bottom: 40px;
					`}
				>
					{props.description}
				</Typography>
				<Flex
					direction="row"
					wrap="wrap"
					css={css`
						width: 100%;
						@media only screen and (max-width: ${theme.breakpoints.xl}) {
						}
					`}
				>
					{props.blogCategories.map((content: BlogCategoryMin, index: number) => {
						return <Intel key={index} {...content} />;
					})}
				</Flex>
				<Spacer
					css={css`
						height: 20px;
					`}
				/>
				<ButtonLink to={props.actionLink} label={props.actionText} />
				<Spacer
					css={css`
						height: 20px;
					`}
				/>
			</ContentSection>
		</>
	);
};
export default MetalMarketIntelligenceDesktop;
